import { Box, IconButton, Typography, useTheme } from '@mui/material'
import React, { useMemo, useState } from 'react'
import UserCard from './UserCard'
import { useMeetingStore } from '../../store/meeting.store'
import { observer } from 'mobx-react'
import { ChevronLeft, ChevronRight, PresentToAll } from '@mui/icons-material'
import { indexToName } from '../../utils/meeting-utilities'
import useWindow from '../../hooks/useWindow'
import CustomSwitch from '../common/CustomSwitch'
import { useMeetingActionsContext } from '../../contexts/meetingactions.context'

const PAGE_SIZE_DESKTOP = 8
const PAGE_SIZE_MOBILE = 4


const gridTemplates = {
  1: {
    xs: `"a"`,                     // 1 column
    md: `"a"`
  },
  2: {
    xs: `"a" "b"`,                 // still single column
    md: `"a b"`
  },
  3: {
    xs: `
      "a b"
      "c c"
    `,                             // → 2 columns
    md: `
      "a a a b b b"
      "a a a c c c"
    `
  },
  4: {
    xs: `
      "a b"
      "c d"
    `,
    md: `
      "a b"
      "c d"
    `
  },
  5: {
    xs: `
      "a b"
      "c d"
      "e e"
    `,
    md: `
      "a a b b c c"
      "d d d e e e"
    `
  },
  6: {
    xs: `
      "a b"
      "c d"
      "e f"
    `,
    md: `
      "a b c"
      "d e f"
    `
  },
  7: {
    xs: `
      "a b"
      "c d"
      "e f"
      "g g"
    `,
    md: `
      "a a b b c c d d"
      "e e e f f f g g"
    `
  },
  8: {
    xs: `
      "a b"
      "c d"
      "e f"
      "g h"
    `,
    md: `
      "a b c d"
      "e f g h"
    `
  }
}


const sharingGridTemplates = {
  1: `"a"`,
  2: {
    md: `
    "a a a a a a b b"
    "a a a a a a b b"
    "a a a a a a b b"
    "a a a a a a b b"
    `,
    xs: `
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      ". b b b b ."
      ". b b b b ."
    `
  },
  3: {
    md: `
    "a a a a a a a b b"
    "a a a a a a a b b"
    "a a a a a a a c c"
    "a a a a a a a c c"
  `,
    sm: `
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      ". b b c c ."
      ". b b c c ."
    `,
    xs: `
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      "b b b c c c"
      "b b b c c c"
    `
  },
  4: {
    md: `
    "a a a a b"
    "a a a a c"
    "a a a a d"
  `,
    xs: `
      "a a a a a a"
      "a a a a a a"
      "a a a a a a"
      "b b c c d d"
    `
  },
  5: {
    md: `
    "a a a a a a a a b b"
    "a a a a a a a a c c"
    "a a a a a a a a d d"
    "a a a a a a a a e e"
  `,
    xs: `
      "a a a a"
      "a a a a"
      "a a a a"
      "b b c c"
      "d d e e"
    `
  },
  6: {
    xs: `
    "a a a a a a"
    "a a a a a a"
    "a a a a a a"
    "b b c c e e"
    "b b d d f f"
  `, md: `
    "a a a a a b c"
    "a a a a a d e"
    "a a a a a f f"
  `},
  7: {
    xs: `
    "a a a a a a"
    "a a a a a a"
    "a a a a a a"
    "b b d d f f"
    "c c e e g g"
  `,
    md: `
    "a a a a a b c"
    "a a a a a d e"
    "a a a a a f g"
  `
  },
  8: {
    md: `
    "a a a a a b c"
    "a a a a a d e"
    "a a a a a f g"
    "a a a a a h h"
  `, xs: `
    "a a a a"
    "a a a a"
    "a a a a"
    "b d f g"
    "c e h h"
  `}
}

function sortUsers ( users ) {
  return [...users].sort( ( a, b ) => {
    // weight function gives priority score

    const weight = ( u ) => {
      if ( u.type === 'local-client' ) return 10
      if ( u.activePresenter ) return 70
      if ( u.isSharing ) return 60
      if ( u.isVideoOn ) return 50
      if ( u.isAudioOn ) return 40
      if ( u.pinned ) return 30
      return 20
    }

    return weight( b ) - weight( a ) // higher weight first
  } )
}

const DiscussionMeetingParticipationLayout = observer( () => {

  const MeetingStore = useMeetingStore()
  const { width: windowWidth } = useWindow()

  const { toggleScreenShareAudio } = useMeetingActionsContext()
  const { palette } = useTheme()

  const [currentPage, setCurrentPage] = useState( 0 )
  const pageSize = windowWidth < 600 ? PAGE_SIZE_MOBILE : PAGE_SIZE_DESKTOP

  const getUsersCard = useMemo( () => {
    const data = {}
    const isRecorder = MeetingStore.getLocalUser.type === 'recorder-client'
    const sortedUsers = sortUsers( MeetingStore.getRemoteUsers )

    data.sharingLayout = MeetingStore.getMeetingState.isSharing || MeetingStore.getMeetingState.isSomeoneSharing || Boolean( MeetingStore.getMeetingState?.activePresenter )
    data.isActivePresenter = MeetingStore.getMeetingState?.activePresenter?.user_id === `${MeetingStore.getLocalUser.user_id}-sharing`
    data.activePresenter = MeetingStore.getMeetingState?.activePresenter

    // In sharing layout the top-weighted user (the active presenter/sharer) is always pinned to the large "a" tile.
    const pinnedFront = data.sharingLayout && sortedUsers.length > 0 ? sortedUsers[0] : null
    const middleSource = pinnedFront ? sortedUsers.slice( 1 ) : sortedUsers
    // Non-recorder clients always keep their own tile on screen, pinned to the last slot.
    const backPinned = isRecorder ? null : MeetingStore.getLocalUser

    const reservedSlots = ( pinnedFront ? 1 : 0 ) + ( backPinned ? 1 : 0 )
    const chunkSize = Math.max( 1, pageSize - reservedSlots )

    data.totalPages = Math.max( 1, Math.ceil( middleSource.length / chunkSize ) )
    data.page = Math.min( currentPage, data.totalPages - 1 )

    const middleSlice = middleSource.slice( data.page * chunkSize, data.page * chunkSize + chunkSize )
    data.cards = reservedSlots + middleSlice.length

    data.tiles = []
    if ( pinnedFront ) data.tiles.push( { user: pinnedFront, area: indexToName( 0 ) } )
    middleSlice.forEach( ( user, i ) => data.tiles.push( { user, area: indexToName( ( pinnedFront ? 1 : 0 ) + i ) } ) )
    if ( backPinned ) data.tiles.push( { user: backPinned, area: indexToName( data.cards - 1 ) } )

    const templates = data.sharingLayout ? sharingGridTemplates : gridTemplates
    data.areasLayout = templates[data.cards]
    return data
  }, [MeetingStore.getMeetingState, MeetingStore.getRemoteUsers, MeetingStore.getLocalUser, pageSize, currentPage] )

  return (
    <Box minHeight={0} flexShrink={1} display="flex" flexDirection="column" id="users-container" flexGrow={1} gap="10px" >
      {getUsersCard.sharingLayout && getUsersCard.activePresenter && <Box borderRadius="10px" bgcolor={palette.greyed.dark} padding="10px 15px">
        {getUsersCard.isActivePresenter && <Box color={palette.greyed.contrastText} display="flex" justifyContent="space-between" alignItems="center" gap="10px">
          <Box display="flex" alignItems="center" gap="10px">
            <PresentToAll sx={{ color: "inherit" }} fontSize='small' />
            <Typography variant='subtitle2' sx={{ color: palette.greyed.contrastText }} fontSize="12px">You are presenting</Typography>
          </Box>
          {MeetingStore.getMeetingState.sharingState.audio && <Box display="flex" alignItems="center" gap="10px">
            <Typography variant='subtitle2' sx={{ color: palette.greyed.contrastText }} fontSize="12px">Share audio</Typography>
            <CustomSwitch color="secondary" size="small" checked={MeetingStore.getMeetingState.sharingState.audioEnabled} onChange={event => {
              toggleScreenShareAudio( event.target.checked )
            }} />
          </Box>}
        </Box>}
        {!getUsersCard.isActivePresenter && <Box>
          <Typography variant='subtitle2' sx={{ color: palette.greyed.contrastText }} fontSize="12px" display="block" >{getUsersCard?.activePresenter?.name || getUsersCard?.activePresenter?.user_name} is presenting</Typography>
        </Box>}
      </Box>}
      {MeetingStore.getMeetingState.isJoined && <Box minHeight={0} display="flex" flexGrow={1} flexDirection={MeetingStore.getMeetingState.isSharing ? "row" : "column"}  >
        <Box
          id="users-grid"
          display="grid"
          gridTemplateAreas={
            getUsersCard.areasLayout
          }
          gridAutoRows="1fr"
          gridAutoColumns="1fr"
          gap="10px"
          flexGrow={1}
          overflow="auto"
          sx={{
            transition: `all 1s ease`
          }}
        >
          {getUsersCard.tiles.map( ( tile ) => (
            <UserCard gridArea={tile.area} key={tile.user.user_id} user={tile.user} />
          )
          )}
        </Box>
      </Box>}
      {getUsersCard.totalPages > 1 && <Box flexShrink={0} display="flex" alignItems="center" justifyContent="center" gap="8px">
        <IconButton size="small" disabled={getUsersCard.page === 0} onClick={() => setCurrentPage( p => Math.max( 0, p - 1 ) )}>
          <ChevronLeft fontSize="small" />
        </IconButton>
        <Typography variant="caption" color="textSecondary">Group {getUsersCard.page + 1} / {getUsersCard.totalPages}</Typography>
        <IconButton size="small" disabled={getUsersCard.page === getUsersCard.totalPages - 1} onClick={() => setCurrentPage( p => Math.min( getUsersCard.totalPages - 1, p + 1 ) )}>
          <ChevronRight fontSize="small" />
        </IconButton>
      </Box>}
    </Box>
  )
} )

export default DiscussionMeetingParticipationLayout
